package com.mgacreative.hotelru.feature.room.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.automirrored.filled.List
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

data class InUseRoom(
    val reservationId: String,
    val roomId: String,
    val roomNumber: String,
    val roomType: String = "",
    val nights: Int,
    val foodCost: String = "0",
    val roomCost: String = "0.0",
    val creditCardCost: String = "0",
    val totalCost: String = "0.0",
    val note: String = "",
    val guestName: String = "",
    val checkOutDate: String = "",
    val status: String = ""
)

@Composable
fun InUseRoomsScreen(
    viewModel: InUseRoomsViewModel = koinViewModel(),
    onNavigate: (String) -> Unit = {}
) {
    val rooms by viewModel.roomStatuses.collectAsState() 
    val isLoading by viewModel.isLoading.collectAsState()
    val gridState = rememberLazyGridState()

    LaunchedEffect(Unit) {
        viewModel.loadRoomStatuses()
    }
    
    val inUseList = remember(rooms) {
        rooms.filter { it.status == "FULL" }.map { 
            InUseRoom(
                reservationId = it.reservationId ?: "",
                roomId = it.id,
                roomNumber = it.roomNumber,
                roomType = it.roomType,
                nights = it.nights,
                foodCost = it.foodCost,
                roomCost = it.roomCost,
                creditCardCost = it.creditCardCost,
                totalCost = it.totalCost,
                guestName = it.guestName,
                status = it.status
            )
        }
    }

    var searchQuery by remember { mutableStateOf("") }
    var expandedRoom by remember { mutableStateOf<InUseRoom?>(null) }
    var roomToCheckOut by remember { mutableStateOf<InUseRoom?>(null) }

    val filteredList = remember(inUseList, searchQuery) {
        if (searchQuery.isBlank()) {
            inUseList
        } else {
            val query = searchQuery.trim().lowercase()
            inUseList.filter { room ->
                room.roomNumber.lowercase().contains(query) ||
                room.guestName.lowercase().contains(query) ||
                room.roomType.lowercase().contains(query)
            }
        }
    }

    Scaffold(
        contentWindowInsets = WindowInsets(0.dp),
        bottomBar = { InUseBottomNavBar(onNavigate = onNavigate) }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF8FAFC))
        ) {
            // Modern Top Bar
            Surface(
                color = Color.White,
                tonalElevation = 1.dp,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(
                            onClick = { onNavigate("DashboardScreen") },
                            modifier = Modifier.size(36.dp)
                        ) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = l10n("Back"), tint = Color(0xFF0F172A))
                        }
                        Spacer(Modifier.width(8.dp))
                        Column {
                            Text(
                                text = l10n("In Use Rooms"),
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF0F172A),
                                fontSize = 17.sp
                            )
                            Text(
                                text = "${inUseList.size} " + l10n("oda şu an konaklamada"),
                                fontSize = 12.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    OutlinedButton(
                        onClick = { viewModel.loadRoomStatuses() },
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = PaddingValues(horizontal = 10.dp, vertical = 4.dp),
                        modifier = Modifier.height(34.dp)
                    ) {
                        Icon(Icons.Default.Refresh, contentDescription = l10n("Refresh"), modifier = Modifier.size(16.dp), tint = Color(0xFF2563EB))
                        Spacer(Modifier.width(4.dp))
                        Text(l10n("Refresh"), fontSize = 12.sp, color = Color(0xFF2563EB), fontWeight = FontWeight.SemiBold)
                    }
                }
            }

            // Search Bar & Filter Section
            Surface(
                modifier = Modifier.fillMaxWidth(),
                color = Color.White,
                border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0))
            ) {
                OutlinedTextField(
                    value = searchQuery,
                    onValueChange = { searchQuery = it },
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 10.dp),
                    placeholder = { Text(l10n("Search by Room No or Guest Name..."), fontSize = 13.sp, color = Color(0xFF94A3B8)) },
                    leadingIcon = {
                        Icon(
                            imageVector = Icons.Default.Search,
                            contentDescription = l10n("Search"),
                            tint = Color(0xFF64748B)
                        )
                    },
                    trailingIcon = {
                        if (searchQuery.isNotEmpty()) {
                            IconButton(onClick = { searchQuery = "" }) {
                                Icon(
                                    imageVector = Icons.Default.Clear,
                                    contentDescription = l10n("Clear"),
                                    tint = Color(0xFF94A3B8)
                                )
                            }
                        }
                    },
                    singleLine = true,
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedContainerColor = Color(0xFFF8FAFC),
                        unfocusedContainerColor = Color(0xFFF8FAFC),
                        focusedBorderColor = Color(0xFF2563EB),
                        unfocusedBorderColor = Color(0xFFE2E8F0)
                    )
                )
            }

            if (isLoading) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = Color(0xFF2563EB))
                }
            } else {
                BoxWithConstraints(
                    modifier = Modifier
                        .fillMaxSize()
                        .weight(1f)
                ) {
                    val columnCount = when {
                        maxWidth >= 1200.dp -> 3
                        maxWidth >= 768.dp -> 2
                        else -> 1
                    }

                    if (filteredList.isEmpty()) {
                        Box(
                            modifier = Modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Icon(Icons.Default.Hotel, contentDescription = null, tint = Color(0xFF94A3B8), modifier = Modifier.size(48.dp))
                                Spacer(modifier = Modifier.height(10.dp))
                                Text(
                                    text = if (searchQuery.isBlank()) l10n("No rooms in use") else l10n("No matching rooms or guests found"),
                                    color = Color(0xFF64748B),
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.Medium
                                )
                            }
                        }
                    } else {
                        LazyVerticalGrid(
                            state = gridState,
                            columns = GridCells.Fixed(columnCount),
                            modifier = Modifier.fillMaxSize().verticalScrollbar(gridState),
                            contentPadding = PaddingValues(16.dp),
                            horizontalArrangement = Arrangement.spacedBy(14.dp),
                            verticalArrangement = Arrangement.spacedBy(14.dp)
                        ) {
                            items(filteredList, key = { it.roomId }) { room ->
                                InUseRoomCard(
                                    room = room,
                                    onMenuClick = { expandedRoom = room },
                                    onCheckOutClick = { roomToCheckOut = room },
                                    onCleaningClick = { viewModel.requestCleaning(room.roomId) },
                                    onPosClick = { onNavigate("RestaurantPosScreen") }
                                )
                            }
                        }
                    }
                }
            }
        }
        
        // Room Action Dialog
        if (expandedRoom != null) {
            val room = expandedRoom!!
            RoomActionBottomSheet(
                room = room,
                onDismiss = { expandedRoom = null },
                onCheckOut = {
                    expandedRoom = null
                    roomToCheckOut = room
                },
                onCleanRequest = {
                    viewModel.requestCleaning(room.roomId)
                    expandedRoom = null
                },
                onAddMenu = {
                    expandedRoom = null
                    onNavigate("RestaurantPosScreen")
                },
                onChangeRoom = {
                    expandedRoom = null
                },
                onEdit = {
                    expandedRoom = null
                }
            )
        }

        // Check-Out Confirmation Dialog
        if (roomToCheckOut != null) {
            val room = roomToCheckOut!!
            AlertDialog(
                onDismissRequest = { roomToCheckOut = null },
                containerColor = Color.White,
                shape = RoundedCornerShape(16.dp),
                title = { Text(l10n("Check-out") + " (${l10n("Oda")} ${room.roomNumber})", fontWeight = FontWeight.Bold, color = Color(0xFF0F172A)) },
                text = {
                    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        Text("${room.guestName.ifBlank { l10n("Misafir") }} " + l10n("için check-out işlemini onaylıyor musunuz?"), color = Color(0xFF475569))
                        if (room.totalCost.toDoubleOrNull() ?: 0.0 > 0.0) {
                            Text(l10n("Toplam Folio Tutarı") + ": ₽ ${room.totalCost}", fontWeight = FontWeight.Bold, color = Color(0xFF059669))
                        }
                    }
                },
                confirmButton = {
                    Button(
                        onClick = {
                            viewModel.checkOut(room.reservationId)
                            roomToCheckOut = null
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Text(l10n("Check-out"), fontWeight = FontWeight.Bold)
                    }
                },
                dismissButton = {
                    OutlinedButton(
                        onClick = { roomToCheckOut = null },
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Text(l10n("İptal"), color = Color(0xFF475569))
                    }
                }
            )
        }
    }
}

@Composable
fun InUseRoomCard(
    room: InUseRoom,
    onMenuClick: () -> Unit,
    onCheckOutClick: () -> Unit,
    onCleaningClick: () -> Unit,
    onPosClick: () -> Unit
) {
    val guestInitials = if (room.guestName.isNotBlank()) {
        room.guestName.split(" ").mapNotNull { it.firstOrNull()?.toString() }.take(2).joinToString("").uppercase()
    } else "M"

    Surface(
        shape = RoundedCornerShape(14.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(14.dp)
        ) {
            // Header: Room Number, Type & Nights
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(8.dp))
                            .background(Color(0xFFFEE2E2))
                            .border(1.dp, Color(0xFFFECACA), RoundedCornerShape(8.dp))
                            .padding(horizontal = 8.dp, vertical = 4.dp)
                    ) {
                        Text(
                            text = "${l10n("Oda")} ${room.roomNumber}",
                            color = Color(0xFFDC2626),
                            fontWeight = FontWeight.Bold,
                            fontSize = 14.sp
                        )
                    }

                    if (room.roomType.isNotBlank()) {
                        Spacer(modifier = Modifier.width(6.dp))
                        Box(
                            modifier = Modifier
                                .clip(RoundedCornerShape(6.dp))
                                .background(Color(0xFFF1F5F9))
                                .padding(horizontal = 6.dp, vertical = 3.dp)
                        ) {
                            Text(
                                text = l10n(room.roomType),
                                fontSize = 11.sp,
                                fontWeight = FontWeight.SemiBold,
                                color = Color(0xFF475569)
                            )
                        }
                    }
                }

                if (room.nights >= 0) {
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(6.dp))
                            .background(Color(0xFFEFF6FF))
                            .padding(horizontal = 8.dp, vertical = 3.dp)
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Default.Schedule, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(12.dp))
                            Spacer(modifier = Modifier.width(4.dp))
                            Text(
                                text = "${room.nights} " + l10n("Nights"),
                                color = Color(0xFF1D4ED8),
                                fontSize = 11.sp,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(10.dp))

            // Guest Name Row
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Box(
                    modifier = Modifier
                        .size(32.dp)
                        .clip(CircleShape)
                        .background(Color(0xFFEFF6FF)),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = guestInitials,
                        fontWeight = FontWeight.Bold,
                        fontSize = 12.sp,
                        color = Color(0xFF2563EB)
                    )
                }

                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = if (room.guestName.isNotBlank()) room.guestName else l10n("Misafir Bilgisi Yok"),
                        fontWeight = FontWeight.Bold,
                        fontSize = 13.sp,
                        color = Color(0xFF0F172A),
                        maxLines = 1
                    )
                    Text(
                        text = l10n("Konaklamada (Dolu)"),
                        fontSize = 11.sp,
                        color = Color(0xFF64748B)
                    )
                }
            }

            Spacer(modifier = Modifier.height(10.dp))
            HorizontalDivider(color = Color(0xFFF1F5F9))
            Spacer(modifier = Modifier.height(8.dp))

            // Cost / Folio Breakdown Grid
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column {
                    Text(l10n("Oda Ücreti"), fontSize = 10.sp, color = Color(0xFF64748B))
                    Text("₽ ${room.roomCost}", fontSize = 12.sp, fontWeight = FontWeight.SemiBold, color = Color(0xFF334155))
                }
                Column {
                    Text(l10n("Restoran / POS"), fontSize = 10.sp, color = Color(0xFF64748B))
                    Text("₽ ${room.foodCost}", fontSize = 12.sp, fontWeight = FontWeight.SemiBold, color = Color(0xFF334155))
                }
                Column(horizontalAlignment = Alignment.End) {
                    Text(l10n("Toplam Tutar"), fontSize = 10.sp, color = Color(0xFF64748B))
                    Text("₽ ${room.totalCost}", fontSize = 13.sp, fontWeight = FontWeight.Bold, color = Color(0xFF059669))
                }
            }

            Spacer(modifier = Modifier.height(12.dp))

            // Action Buttons Row
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                OutlinedButton(
                    onClick = onPosClick,
                    shape = RoundedCornerShape(8.dp),
                    contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                    modifier = Modifier.weight(1f).height(32.dp)
                ) {
                    Icon(Icons.Default.RestaurantMenu, contentDescription = null, modifier = Modifier.size(13.dp), tint = Color(0xFF2563EB))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(l10n("POS"), fontSize = 11.sp, color = Color(0xFF2563EB), fontWeight = FontWeight.SemiBold)
                }

                OutlinedButton(
                    onClick = onCleaningClick,
                    shape = RoundedCornerShape(8.dp),
                    contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                    modifier = Modifier.weight(1f).height(32.dp)
                ) {
                    Icon(Icons.Default.CleaningServices, contentDescription = null, modifier = Modifier.size(13.dp), tint = Color(0xFFD97706))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(l10n("Temizlik"), fontSize = 11.sp, color = Color(0xFFD97706), fontWeight = FontWeight.SemiBold)
                }

                Button(
                    onClick = onCheckOutClick,
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                    shape = RoundedCornerShape(8.dp),
                    contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                    modifier = Modifier.weight(1.2f).height(32.dp)
                ) {
                    Icon(Icons.Default.ExitToApp, contentDescription = null, modifier = Modifier.size(13.dp), tint = Color.White)
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(l10n("Check-out"), fontSize = 11.sp, color = Color.White, fontWeight = FontWeight.Bold)
                }

                IconButton(
                    onClick = onMenuClick,
                    modifier = Modifier
                        .size(32.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(Color(0xFFF1F5F9))
                ) {
                    Icon(Icons.Default.MoreVert, contentDescription = l10n("Daha Fazla"), tint = Color(0xFF475569), modifier = Modifier.size(16.dp))
                }
            }
        }
    }
}

@Composable
fun RoomActionBottomSheet(
    room: InUseRoom,
    onDismiss: () -> Unit,
    onCheckOut: () -> Unit,
    onCleanRequest: () -> Unit,
    onAddMenu: () -> Unit,
    onChangeRoom: () -> Unit,
    onEdit: () -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(16.dp),
        title = { Text(l10n("Room Actions") + " (${l10n("Oda")} ${room.roomNumber})", fontWeight = FontWeight.Bold, color = Color(0xFF0F172A)) },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                BottomSheetMenuItem(icon = Icons.Default.Edit, title = l10n("Edit"), onClick = { onDismiss(); onEdit() })
                BottomSheetMenuItem(icon = Icons.Default.RestaurantMenu, title = l10n("Add menu"), onClick = { onDismiss(); onAddMenu() })
                BottomSheetMenuItem(icon = Icons.Default.CleaningServices, title = l10n("Cleaning request"), onClick = { onDismiss(); onCleanRequest() })
                BottomSheetMenuItem(icon = Icons.Default.SwapHoriz, title = l10n("Change room"), onClick = { onDismiss(); onChangeRoom() })
                BottomSheetMenuItem(icon = Icons.Default.ExitToApp, title = l10n("Check-out"), iconTint = Color(0xFFDC2626), titleColor = Color(0xFFDC2626), onClick = { onDismiss(); onCheckOut() })
            }
        },
        confirmButton = {
            OutlinedButton(
                onClick = onDismiss,
                shape = RoundedCornerShape(8.dp)
            ) {
                Text(l10n("Close"), color = Color(0xFF475569))
            }
        }
    )
}

@Composable
fun BottomSheetMenuItem(
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    title: String,
    iconTint: Color = Color(0xFF2563EB),
    titleColor: Color = Color(0xFF0F172A),
    onClick: () -> Unit
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .clickable { onClick() }
            .padding(horizontal = 10.dp, vertical = 10.dp)
    ) {
        Icon(icon, contentDescription = null, modifier = Modifier.size(20.dp), tint = iconTint)
        Spacer(modifier = Modifier.width(12.dp))
        Text(title, fontSize = 14.sp, color = titleColor, fontWeight = FontWeight.Medium)
    }
}

@Composable
fun InUseBottomNavBar(onNavigate: (String) -> Unit) {
    NavigationBar(
        containerColor = Color.White,
        tonalElevation = 4.dp
    ) {
        NavigationBarItem(
            icon = { Icon(Icons.AutoMirrored.Filled.List, null) },
            label = { Text(l10n("Reservation"), fontSize = 10.sp) },
            selected = false,
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2563EB),
                selectedTextColor = Color(0xFF2563EB),
                indicatorColor = Color(0xFFEFF6FF)
            ),
            onClick = { onNavigate("NewReservationCalendarScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.DateRange, null) },
            label = { Text(l10n("Occupancy"), fontSize = 10.sp) },
            selected = false,
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2563EB),
                selectedTextColor = Color(0xFF2563EB),
                indicatorColor = Color(0xFFEFF6FF)
            ),
            onClick = { onNavigate("ReservationCalendarScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.AutoMirrored.Filled.ArrowForward, null) },
            label = { Text(l10n("Check-in"), fontSize = 10.sp) },
            selected = false,
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2563EB),
                selectedTextColor = Color(0xFF2563EB),
                indicatorColor = Color(0xFFEFF6FF)
            ),
            onClick = { onNavigate("CheckInListScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.LocationOn, null) },
            label = { Text(l10n("In Use"), fontSize = 10.sp, fontWeight = FontWeight.Bold) },
            selected = true,
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2563EB),
                selectedTextColor = Color(0xFF2563EB),
                indicatorColor = Color(0xFFEFF6FF)
            ),
            onClick = { onNavigate("InUseRoomsScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.Assessment, null) },
            label = { Text(l10n("Room Summary"), fontSize = 10.sp) },
            selected = false,
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2563EB),
                selectedTextColor = Color(0xFF2563EB),
                indicatorColor = Color(0xFFEFF6FF)
            ),
            onClick = { onNavigate("RoomSummaryScreen") }
        )
    }
}
